import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import classes from "./EditLyric.module.css";
import Song from "../../../types/Song";
import { UserContext } from "../../../contexts/AuthContext";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "../../../components/common/Input";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

interface EditLyricProps {
  onComplete?: () => void;
}

const EditLyric: React.FC<EditLyricProps> = ({ onComplete }) => {
  const location = useLocation();
  const { songId, from } = location.state || {};
const songIdToEdit = songId;
  const navigate = useNavigate();
  const context = useContext(UserContext);
  const [text, setText] = useState("");

  // Validation schema
  const schema = yup.object().shape({
    lyrics: yup
    .string()
    .test("not-empty", "Lyrics are required", (value = "") => {
        // Remove HTML tags and check length
        return value.replace(/<[^>]+>/g, "").trim().length > 0;
    }),
    lyricist: yup.string(),
    credits: yup.string(),
    englishTitle: yup.string().required("English title is required"),
    teluguTitle: yup.string().required("Telugu title is required"),
    status: yup
      .string()
      .oneOf(["ACTIVE", "INACTIVE"], "Invalid status")
      .required("Status is required"),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { status: "ACTIVE" },
  });

  // Fetch song data by ID and prefill form
  useEffect(() => {
    if (!songIdToEdit) return;

    const fetchSong = async () => {
      try {
        const res = await fetch(`${apiUrl}/lyrics/${songIdToEdit}`, {
          headers: {
            Authorization: `Bearer ${context.token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error("Failed to fetch song");

        const data: Song = await res.json();

        setValue("englishTitle", data.title.english);
        setValue("teluguTitle", data.title.telugu);
        setValue("lyricist", data.lyricist);
        setValue("credits", data.credits);
        setValue("status", data.status as "ACTIVE" | "INACTIVE");
        setText(data.lyrics || "");
setValue("lyrics", data.lyrics || "");
      } catch (err) {
        console.error(err);
        toast.error("Failed to load song");
      }
    };

    fetchSong();
  }, [songIdToEdit]);

  // Update song on submit
  const onSubmit = (data: any) => {
    const updateSong = async () => {
      try {
        const res = await fetch(`${apiUrl}/lyrics/${songIdToEdit}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${context.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            lyricist: data.lyricist,
            title: {
              english: data.englishTitle,
              telugu: data.teluguTitle,
            },
            lyrics: data.lyrics,
            credits: data.credits,
            status: data.status,
          }),
        });

        if (!res.ok) throw new Error("Failed to update song");

        toast.success("Song updated successfully");
        setTimeout(()=> {
          navigate(from || "/dashboard/all-songs")
        }, 1000)
        if (onComplete) onComplete();
      } catch (err) {
        console.error(err);
        toast.error("Failed to update song");
      }
    };

    updateSong();
  };

  return (
    <>
      <div className={classes.addSongWraper}>
        <ToastContainer />
        <div className="max-w-md mx-auto p-6 bg-white shadow-md rounded-lg">
          <h2 className="text-xl font-bold mb-4">Edit Lyric</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className={classes.input}>
              <Input type="text" label="English Title" {...register("englishTitle")} />
              {errors.englishTitle && (
                <p className={classes.formError}>{errors.englishTitle.message}</p>
              )}
            </div>

            <div className={classes.input}>
              <Input type="text" label="Telugu Title" {...register("teluguTitle")} />
              {errors.teluguTitle && (
                <p className={classes.formError}>{errors.teluguTitle.message}</p>
              )}
            </div>

            <div className={classes.input}>
              <Input type="text" label="Lyricist" {...register("lyricist")} />
              {errors.lyricist && (
                <p className={classes.formError}>{errors.lyricist.message}</p>
              )}
            </div>

            <div className={classes.input}>
              <Input type="text" label="Credits" {...register("credits")} />
              {errors.credits && (
                <p className={classes.formError}>{errors.credits.message}</p>
              )}
            </div>

            <div className={classes.input}>
              <div className={classes.input}>
                  <label className="block font-medium mb-1">Lyrics</label>

                  <Controller
                    name="lyrics"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <ReactQuill
                        theme="snow"
                        value={text}
                        onChange={(value) => {
                          setText(value);
                          field.onChange(value);
                        }}
                        style={{ height: 300, marginBottom: 40 }}
                      />
                    )}
                  />

                  {errors.lyrics && (
                    <p className={classes.formError}>{errors.lyrics.message}</p>
                  )}
                </div>
            </div>

            <div className={classes.input}>
              <label className="block font-medium">Status</label> <br />
              <select {...register("status")} className="w-full p-2 border rounded">
                <option value="">Select Status</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
              <p className={classes.formError}>{errors.status?.message}</p>
            </div>

            <button type="submit" className={classes.submitButton}>
              Update Song
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditLyric;
